import { isEqual } from 'lodash';
import {
  ActivityFieldLayoutItem,
  ActivitySectionLayoutItem,
  VisibilityRule,
} from './types';

export const deleteLayoutItemFromVisibilityRules = (
  visibilityRules: VisibilityRule[],
  item: VisibilityRule['layout_items'][number]
) => {
  const result: VisibilityRule[] = [];

  if ('field_id' in item) {
    for (const vr of visibilityRules) {
      const layout_items = vr.layout_items.filter((element) => {
        if ('internal_name' in element) {
          return true;
        }

        return element.field_id !== item.field_id;
      });
      const rule = {
        and: vr.rule.and,
        filters: vr.rule.filters.filter(
          (x) => x.activity_field_id !== item.field_id
        ),
      };
      result.push({ layout_items, rule });
    }
  }

  if ('internal_name' in item) {
    for (const vr of visibilityRules) {
      const layout_items = vr.layout_items.filter((element) => {
        if ('field_id' in element) {
          return true;
        }

        return element.internal_name !== item.internal_name;
      });
      result.push({ layout_items, rule: vr.rule });
    }
  }

  return result;
};

export const isVisibilityRuleInvalid = (vr: VisibilityRule) => {
  return vr.layout_items.length === 0 || vr.rule.filters.length === 0;
};

export const hasCyclicRules = (visibilityRules: VisibilityRule[]) => {
  const relationships = visibilityRules.reduce<Map<string, string[]>>(
    (acc, vr) => {
      for (const { activity_field_id } of vr.rule.filters) {
        const hidden = vr.layout_items.reduce<string[]>((acc, element) => {
          if ('field_id' in element) {
            acc.push(element.field_id);
          }
          return acc;
        }, []);
        if (acc.has(activity_field_id)) {
          acc.get(activity_field_id)!.push(...hidden);
        } else {
          acc.set(activity_field_id, hidden);
        }
      }
      return acc;
    },
    new Map()
  );

  const hasCycle = (testIds: string[], hiddenByTestId?: string[]): boolean => {
    if (!hiddenByTestId) {
      return false;
    }
    if (hiddenByTestId.some((x) => testIds.includes(x))) {
      return true;
    }
    return hiddenByTestId.some((x) => {
      return hasCycle(testIds.concat(x), relationships.get(x));
    });
  };

  for (const item of visibilityRules.flatMap((x) => x.layout_items)) {
    if ('field_id' in item) {
      if (hasCycle([item.field_id], relationships.get(item.field_id))) {
        return true;
      }
    }
  }

  return false;
};

export const visibilityRuleEq = (
  r1: VisibilityRule,
  r2: VisibilityRule,
  opts: { compareFields: boolean } = { compareFields: true }
) => {
  if (opts?.compareFields) {
    const f1 = new Set(
      r1.layout_items.map((x) => {
        return 'field_id' in x ? x.field_id : x.internal_name;
      })
    );
    const f2 = new Set(
      r2.layout_items.map((x) => {
        return 'field_id' in x ? x.field_id : x.internal_name;
      })
    );

    if (f1.size !== f2.size) {
      return false;
    }
  }

  if (r1.rule.and !== r2.rule.and) {
    return false;
  }

  if (r1.rule.filters.length !== r2.rule.filters.length) {
    return false;
  }

  for (let i = 0; i < r1.rule.filters.length; i++) {
    const { value: a_value, view_model: _vma, ...a } = r1.rule.filters[i];
    const { value: b_value, view_model: _vmb, ...b } = r2.rule.filters[i];

    if (Array.isArray(a_value) && Array.isArray(b_value)) {
      if (new Set(a_value).size !== new Set(b_value).size) {
        return false;
      }
    }

    if (!isEqual(a, b)) {
      return false;
    }
  }

  return true;
};

export const isFieldLayoutItem = (
  item: any
): item is ActivityFieldLayoutItem => {
  return item.type === 'field';
};

export const isSectionLayoutItem = (
  item: any
): item is ActivitySectionLayoutItem => {
  return item.type === 'section';
};
